// Admin data-access (ported from CreatINN CourseEditorClient + AdminDashboardClient,
// rewritten for Supabase JS direct table access with RLS admin policies).
import { supabase } from '../integrations/supabase/client';
import { getCourseWithContent } from './lms';
import type { Certificate } from '../types/assess';
import type { LmsCourse } from '../types/lms';

export interface CourseRow {
  id: string;
  slug: string;
  title: string;
  description: string;
  tagline: string;
  thumbnail: string | null;
  price_value: number;
  duration: string;
  level: string;
  category: string;
  is_published: boolean;
  is_featured: boolean;
}

export interface StudentRow {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  role: string;
  enrollments: { course_id: string; status: string; enrolled_at: string }[];
}

export async function listCoursesAdmin(): Promise<(CourseRow & { enrollments: number; modules: number })[]> {
  const { data } = await supabase.from('courses').select('*').order('price_value');
  const courses = ((data ?? []) as unknown as CourseRow[]);
  const out: (CourseRow & { enrollments: number; modules: number })[] = [];
  for (const c of courses) {
    let eCount = 0;
    let mCount = 0;
    try {
      const [eRes, mRes] = await Promise.all([
        Promise.resolve(supabase.from('enrollments').select('*', { count: 'exact', head: true }).eq('course_id', c.id)),
        Promise.resolve(supabase.from('modules').select('*', { count: 'exact', head: true }).eq('course_id', c.id)),
      ]);
      eCount = (eRes as unknown as { count: number }).count ?? 0;
      mCount = (mRes as unknown as { count: number }).count ?? 0;
    } catch {
      eCount = 0;
      mCount = 0;
    }
    out.push({ ...c, enrollments: eCount, modules: mCount });
  }
  return out;
}

export async function getCourseAdmin(courseId: string): Promise<LmsCourse | null> {
  return getCourseWithContent(courseId);
}

export async function updateCourseAdmin(courseId: string, patch: Partial<CourseRow>) {
  const { error } = await supabase.from('courses').update(patch).eq('id', courseId);
  if (error) throw new Error(error.message);
}

export async function createCourseAdmin(input: { title: string; price_value: number; duration?: string; level?: string; category?: string; description?: string; is_published?: boolean; is_featured?: boolean }) {
  const slugBase = input.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  const id = slugBase || `course-${Date.now()}`;
  const { error } = await supabase.from('courses').insert({
    id,
    slug: slugBase || id,
    title: input.title,
    description: input.description ?? '',
    tagline: '',
    thumbnail: null,
    price_value: input.price_value,
    duration: input.duration ?? '',
    level: input.level ?? 'Beginner',
    category: input.category ?? 'Certificate',
    is_published: input.is_published ?? false,
    is_featured: input.is_featured ?? true,
  });
  if (error) throw new Error(error.message);
  return id;
}

export async function deleteCourseAdmin(courseId: string) {
  const { error } = await supabase.from('courses').delete().eq('id', courseId);
  if (error) throw new Error(error.message);
}

export async function createModuleAdmin(courseId: string, title: string, description = '') {
  const { data: existing } = await supabase.from('modules').select('position').eq('course_id', courseId).order('position', { ascending: false }).limit(1);
  const pos = ((existing ?? []) as unknown as { position: number }[])[0]?.position ?? -1;
  const { data, error } = await supabase.from('modules').insert({ course_id: courseId, title, description, position: pos + 1 }).select().single();
  if (error) throw new Error(error.message);
  return data;
}

export async function deleteModuleAdmin(moduleId: string) {
  const { error } = await supabase.from('modules').delete().eq('id', moduleId);
  if (error) throw new Error(error.message);
}

export async function createLessonAdmin(moduleId: string, input: { title: string; video_url?: string | null; duration?: string; content?: string; is_free?: boolean; resources?: string }) {
  const { data: existing } = await supabase.from('lessons').select('position').eq('module_id', moduleId).order('position', { ascending: false }).limit(1);
  const pos = ((existing ?? []) as unknown as { position: number }[])[0]?.position ?? -1;
  const { data, error } = await supabase.from('lessons').insert({
    module_id: moduleId,
    title: input.title,
    video_url: input.video_url ?? null,
    duration: input.duration ?? '',
    content: input.content ?? '',
    position: pos + 1,
    is_free: input.is_free ?? false,
    resources: input.resources ?? '',
  }).select().single();
  if (error) throw new Error(error.message);
  return data;
}

export async function updateLessonAdmin(lessonId: string, patch: { title?: string; video_url?: string | null; duration?: string; content?: string; is_free?: boolean; resources?: string }) {
  const { error } = await supabase.from('lessons').update(patch).eq('id', lessonId);
  if (error) throw new Error(error.message);
}

export async function deleteLessonAdmin(lessonId: string) {
  const { error } = await supabase.from('lessons').delete().eq('id', lessonId);
  if (error) throw new Error(error.message);
}

export async function listStudentsAdmin(): Promise<StudentRow[]> {
  const { data, error } = await supabase.from('profiles').select('*').order('created_at', { ascending: false }).limit(200);
  if (error) throw new Error(error.message);
  const profiles = (data ?? []) as unknown as { id: string; full_name: string; email: string | null; phone: string | null; role: string }[];
  const out: StudentRow[] = [];
  for (const p of profiles) {
    const { data: enr } = await supabase.from('enrollments').select('course_id,status,enrolled_at').eq('user_id', p.id);
    out.push({ ...p, enrollments: ((enr ?? []) as unknown as StudentRow['enrollments']) });
  }
  return out;
}

export async function setEnrollmentStatus(userId: string, courseId: string, status: 'active' | 'revoked' | 'completed') {
  const { error } = await supabase.from('enrollments').update({ status }).eq('user_id', userId).eq('course_id', courseId);
  if (error) throw new Error(error.message);
}

async function countRows(table: 'profiles' | 'courses' | 'enrollments' | 'certificates'): Promise<number> {
  try {
    const res = await Promise.resolve(supabase.from(table).select('*', { count: 'exact', head: true }));
    return (res as unknown as { count: number }).count ?? 0;
  } catch {
    return 0;
  }
}

async function countPendingSubmissions(): Promise<number> {
  try {
    const res = await Promise.resolve(supabase.from('assignment_submissions').select('*', { count: 'exact', head: true }).eq('status', 'submitted'));
    return (res as unknown as { count: number }).count ?? 0;
  } catch {
    return 0;
  }
}

async function completedRevenue(): Promise<number> {
  try {
    const { data } = await supabase.from('payments').select('amount').eq('status', 'completed').limit(5000);
    return ((data ?? []) as unknown as { amount: number }[]).reduce((sum, p) => sum + (p.amount || 0), 0);
  } catch {
    return 0;
  }
}

export async function adminStats() {
  const [students, courses, enrollments, certificates, pendingGrading, revenue] = await Promise.all([
    countRows('profiles'),
    countRows('courses'),
    countRows('enrollments'),
    countRows('certificates'),
    countPendingSubmissions(),
    completedRevenue(),
  ]);
  return { students, courses, enrollments, certificates, pendingGrading, revenue };
}

export interface SubmissionRow {
  id: string;
  user_id: string;
  assignment_id: string;
  content: string;
  file_url: string | null;
  status: string;
  grade: number | null;
  feedback: string;
  created_at: string;
  student_name: string;
  assignment_title: string;
  course_id: string;
  course_title: string;
}

export async function listSubmissionsAdmin(status: 'submitted' | 'all' = 'submitted'): Promise<SubmissionRow[]> {
  let query = supabase.from('assignment_submissions').select('*').order('created_at', { ascending: false }).limit(200);
  if (status !== 'all') query = query.eq('status', status);
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  const rows = ((data ?? []) as unknown as Omit<SubmissionRow, 'student_name' | 'assignment_title' | 'course_id' | 'course_title'>[]);
  const out: SubmissionRow[] = [];
  for (const r of rows) {
    let studentName = r.user_id.slice(0, 8);
    let assignmentTitle = r.assignment_id.slice(0, 8);
    let courseId = '';
    let courseTitle = '';
    try {
      const { data: p } = await supabase.from('profiles').select('full_name').eq('id', r.user_id).maybeSingle();
      if (p) studentName = (p as unknown as { full_name: string }).full_name || studentName;
    } catch { /* ignore */ }
    try {
      const { data: a } = await supabase.from('assignments').select('title,course_id').eq('id', r.assignment_id).maybeSingle();
      const assignment = a as unknown as { title: string; course_id: string } | null;
      if (assignment) {
        assignmentTitle = assignment.title;
        courseId = assignment.course_id;
        const { data: c } = await supabase.from('courses').select('title').eq('id', courseId).maybeSingle();
        if (c) courseTitle = (c as unknown as { title: string }).title;
      }
    } catch { /* ignore */ }
    out.push({ ...r, student_name: studentName, assignment_title: assignmentTitle, course_id: courseId, course_title: courseTitle });
  }
  return out;
}

export async function gradeSubmissionAdmin(submissionId: string, grade: number | null, feedback: string, status: 'graded' | 'returned' = 'graded') {
  const { error } = await supabase.from('assignment_submissions').update({ grade, feedback, status }).eq('id', submissionId);
  if (error) throw new Error(error.message);
}

export async function grantEnrollmentAdmin(userId: string, courseId: string) {
  const { error } = await supabase.from('enrollments').upsert(
    { user_id: userId, course_id: courseId, status: 'active', paystack_ref: 'admin-grant' },
    { onConflict: 'user_id,course_id' }
  );
  if (error) throw new Error(error.message);
}

export async function listCertificatesAdmin(): Promise<Certificate[]> {
  const { data, error } = await supabase.from('certificates').select('*').order('issued_at', { ascending: false }).limit(500);
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as unknown as Certificate[];
  const userIds = [...new Set(rows.map((r) => r.user_id))];
  const courseIds = [...new Set(rows.map((r) => r.course_id))];
  const names: Record<string, string> = {};
  const titles: Record<string, string> = {};
  if (userIds.length > 0) {
    const { data: people } = await supabase.from('profiles').select('id,full_name').in('id', userIds);
    (people ?? []).forEach((p) => {
      const row = p as unknown as { id: string; full_name: string };
      names[row.id] = row.full_name;
    });
  }
  if (courseIds.length > 0) {
    const { data: courses } = await supabase.from('courses').select('id,title').in('id', courseIds);
    (courses ?? []).forEach((c) => {
      const row = c as unknown as { id: string; title: string };
      titles[row.id] = row.title;
    });
  }
  return rows.map((r) => ({ ...r, student_name: names[r.user_id] || r.user_id.slice(0, 8), course_title: titles[r.course_id] || r.course_id }));
}

export async function revokeCertificateAdmin(certificateId: string, reason: string) {
  const { error } = await supabase
    .from('certificates')
    .update({ revoked: true, revoked_at: new Date().toISOString(), revoked_reason: reason || null })
    .eq('id', certificateId);
  if (error) throw new Error(error.message);
}

export async function restoreCertificateAdmin(certificateId: string) {
  const { error } = await supabase
    .from('certificates')
    .update({ revoked: false, revoked_at: null, revoked_reason: null })
    .eq('id', certificateId);
  if (error) throw new Error(error.message);
}
